/**
 * The NPC weapon stat block, normalized across the two pack layouts.
 *
 * 54 features in each shipped pack are Weapons whose `effect` is empty or absent — **all** of
 * their content is in these fields, which is why they rendered as blank record sheets. This reads
 * them at a selected tier.
 *
 * **Two key-name divergences, both measured, both silent if you read one side only.** They are
 * handled here at the point of use rather than by a normalization pass at ingest, deliberately:
 * the v3 adapter already reconstructs the class→feature edges on the way in, and a second
 * rewrite-the-archive step would put two different "what the data really is" answers in the repo.
 *
 * 1. **Per-tier damage lives under a different key in each layout** — v2 writes
 *    `damage[].damage`, v3 writes `damage[].val`. Measured over the fixtures: 54 v2 entries carry
 *    `damage`, 55 v3 entries carry `val`, and neither pack carries the other's spelling. Read only
 *    one and half the weapons in one pack lose their damage with no error.
 * 2. **One v3 record spells a range value `value`** where the other 32 in that position use `val`
 *    (inside `actions[].range[]`; the top-level `range[]` uses `val` in both packs, 72 v2 / 74 v3).
 *    A single typo'd record, and reading only `val` drops that one range band silently.
 *
 * The other shape variation is scalar-vs-array, everywhere: v2 arrays every per-tier number, v3
 * collapses the tier-invariant ones to a bare scalar (`attack_bonus` is `array<number>` 66 times
 * and `number` 3 times; `accuracy` is `number` 20 times and `array<number>` 4). {@link statAtTier}
 * treats a scalar as tier-invariant, which is the whole reason it is shared with the stat array.
 */
import { interpolateTierTokens, statAtTier, type NpcTier } from "../../lib/npcTierText";

/** One damage entry at the selected tier — `value` is already tier-resolved and formatted. */
export type NpcDamageView = {
  type: string;
  value: string;
  aoe?: string;
  ap?: boolean;
  save?: string;
  saveHalf?: boolean;
};

/** One range band, pre-formatted for display ("Range 10", "Blast 1"). */
export type NpcRangeView = { label: string };

/** A feature's weapon stat block, tier-resolved. Absent when the feature carries no weapon fields. */
export type NpcWeaponView = {
  weaponType?: string;
  attackBonus?: string;
  accuracy?: string;
  attacks?: string;
  damage: NpcDamageView[];
  range: NpcRangeView[];
  onHitHtml?: string;
  onCritHtml?: string;
};

/** How a caller supplies the tier and its prose formatter, kept as one bag for the 3-param lint. */
export type NpcWeaponCtx = {
  tier: NpcTier;
  /** The caller's tier-interpolating formatter — prose in this block carries `{…}` tokens too. */
  text: (raw: string | undefined) => string;
};

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;

const asString = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);

/** Wrap a bare object in a one-element list, so the object and array forms share one reader. */
function asList(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  return v === null || v === undefined ? [] : [v];
}

/**
 * Format a per-tier number for display. A string value is passed through the tier formatter
 * instead — a few entries carry a `{4/6/8}` token where others carry a real array.
 */
function tierNumber(raw: unknown, ctx: NpcWeaponCtx): string | undefined {
  if (typeof raw === "string") {
    const resolved = ctx.text(raw);
    return resolved === "" ? undefined : resolved;
  }
  const at = statAtTier(raw, ctx.tier);
  return at === undefined ? undefined : String(at);
}

/** Same, with an explicit sign — attack bonus and accuracy read as modifiers, not quantities. */
function tierSigned(raw: unknown, ctx: NpcWeaponCtx): string | undefined {
  const v = tierNumber(raw, ctx);
  if (v === undefined) return undefined;
  return /^[-+]/.test(v) ? v : `+${v}`;
}

function damageViews(raw: unknown, ctx: NpcWeaponCtx): NpcDamageView[] {
  const out: NpcDamageView[] = [];
  for (const entry of asList(raw)) {
    const e = asRecord(entry);
    if (!e) continue;
    // `val` is v3's key, `damage` is v2's. Neither pack carries the other's spelling, so the
    // coalesce is exact rather than a guess about precedence.
    const value = tierNumber(e.val ?? e.damage, ctx);
    const type = asString(e.type);
    if (value === undefined && type === undefined) continue;
    out.push({
      type: type ?? "",
      value: value ?? "",
      aoe: typeof e.aoe === "string" ? e.aoe : undefined,
      ap: e.ap === true ? true : undefined,
      save: asString(e.save),
      saveHalf: e.save_half === true ? true : undefined,
    });
  }
  return out;
}

function rangeViews(raw: unknown): NpcRangeView[] {
  const out: NpcRangeView[] = [];
  for (const entry of asList(raw)) {
    const e = asRecord(entry);
    if (!e) continue;
    // `value` is the one-record typo variant; see this module's header.
    const val = typeof e.val === "number" ? e.val : typeof e.value === "number" ? e.value : undefined;
    const type = asString(e.type);
    if (type === undefined && val === undefined) continue;
    out.push({ label: [type, val].filter((x) => x !== undefined).join(" ") });
  }
  return out;
}

/**
 * Normalize an on-hit / on-crit clause to prose. Three forms occur: a plain string (v2's 54
 * records, many of them empty), an object whose `detail` carries the text (v3's 9), and a list of
 * named clauses (1 record). The structured siblings — `add_status`, `add_special`, `save` — are
 * separate fields owed to the Phase 4 tail; `detail` already spells out the same rule in prose on
 * every record that has them, so nothing is lost by rendering prose alone here.
 */
function clauseHtml(raw: unknown, ctx: NpcWeaponCtx): string | undefined {
  if (typeof raw === "string") return ctx.text(raw) || undefined;
  const parts: string[] = [];
  for (const entry of asList(raw)) {
    const e = asRecord(entry);
    if (!e) continue;
    const detail = ctx.text(asString(e.detail));
    if (!detail) continue;
    const name = asString(e.name);
    parts.push(name ? `<b>${name}.</b> ${detail}` : detail);
  }
  return parts.length > 0 ? parts.join("<br/>") : undefined;
}

/**
 * Build a feature's weapon block at `ctx.tier`, or `undefined` when the feature carries none of
 * these fields. Returning `undefined` rather than an empty block is what lets the caller decide
 * not to render a section header for a non-weapon feature.
 */
export function npcWeaponView(raw: unknown, ctx: NpcWeaponCtx): NpcWeaponView | undefined {
  const f = asRecord(raw);
  if (!f) return undefined;
  const view: NpcWeaponView = {
    weaponType: asString(f.weapon_type),
    attackBonus: tierSigned(f.attack_bonus, ctx),
    accuracy: tierSigned(f.accuracy, ctx),
    attacks: tierNumber(f.attacks, ctx),
    damage: damageViews(f.damage, ctx),
    range: rangeViews(f.range),
    onHitHtml: clauseHtml(f.on_hit, ctx),
    onCritHtml: clauseHtml(f.on_crit, ctx),
  };
  const empty =
    view.weaponType === undefined &&
    view.attackBonus === undefined &&
    view.accuracy === undefined &&
    view.attacks === undefined &&
    view.damage.length === 0 &&
    view.range.length === 0 &&
    view.onHitHtml === undefined &&
    view.onCritHtml === undefined;
  return empty ? undefined : view;
}

/** Convenience for a caller that only has a tier: the standard formatter, no owning-class stats. */
export function plainTierText(tier: NpcTier): (raw: string | undefined) => string {
  return (rawText) => interpolateTierTokens(rawText ?? "", tier);
}
